/**
 * The KDF record and its parameters (decision 25; ADR 4, amended 2026-10-21; part 25a log, tables K and R). An account's
 * record is its salt, its Argon2id memory and passes, and the KDF version, which fixes everything else: the Argon2id
 * build, its parallelism, the output length and the HKDF labels. The relay hands the record out at login, so a device
 * refuses parameters below the floor (a relay making the login secret cheap to crack), above the ceiling (a relay making
 * the phone run out of memory) or below the highest it has seen for the account (a downgrade after an upgrade).
 */

/** Argon2id's cost: memory in KiB and passes over it. */
export interface KdfParameters {
  readonly memoryKiB: number;
  readonly passes: number;
}

/** An account's KDF record (decision 25): the KDF version, a random salt and the Argon2id parameters. */
export interface KdfRecord extends KdfParameters {
  readonly version: KdfVersion;
  readonly salt: Uint8Array<ArrayBuffer>;
}

/** The KDF versions this build knows; each is pinned by known-answer tests (`login.test.ts`). */
export type KdfVersion = 1;

/**
 * KDF version 1: Argon2id v1.3 (hash-wasm 4.12.0) with parallelism 1 and a 16-byte salt, giving 32 bytes; HKDF-SHA-256
 * with no salt splits them by the labels in `login.ts`.
 */
export const KDF_V1 = { version: 1, parallelism: 1, saltBytes: 16, outputBytes: 32 } as const;

/** K1, K2: the hard-coded floor (decision 25): 64 MiB and 3 passes. */
export const KDF_FLOOR: KdfParameters = { memoryKiB: 64 * 1024, passes: 3 };

/**
 * K3, K4: the hard-coded ceiling (decision 25): 256 MiB and 10 passes. Provisional until the phone benchmark (encryption
 * council 2; part 25a log, open questions): the ceiling is pinned only after it.
 */
export const KDF_CEILING: KdfParameters = { memoryKiB: 256 * 1024, passes: 10 };

/** The parameters a new account gets: the floor until the phone benchmark tunes them to about 1 s (decision 25). */
export const KDF_DEFAULT: KdfParameters = KDF_FLOOR;

/** Why parameters were refused: below the floor, above the ceiling, or below the highest seen for the account. */
export type ParameterRefusal = 'below-floor' | 'above-ceiling' | 'below-highest';

/** Parameters taken, with the highest seen to remember for the account; or refused, with why. */
export type ParameterCheck = { readonly kind: 'accepted'; readonly highest: KdfParameters } | { readonly kind: 'refused'; readonly reason: ParameterRefusal };

/**
 * Table K: checks `parameters` against the floor and the ceiling, then against `highest`, the highest this device has
 * seen for the account (null for none). Each of memory and passes is compared alone, so neither can be traded for the
 * other (K6, K7). Taken parameters are at least `highest` in both, so they become the highest seen (K5, K8, K9).
 */
export function checkParameters(parameters: KdfParameters, highest: KdfParameters | null): ParameterCheck {
  if (parameters.memoryKiB < KDF_FLOOR.memoryKiB || parameters.passes < KDF_FLOOR.passes) return { kind: 'refused', reason: 'below-floor' };
  if (parameters.memoryKiB > KDF_CEILING.memoryKiB || parameters.passes > KDF_CEILING.passes) return { kind: 'refused', reason: 'above-ceiling' };
  if (highest !== null && (parameters.memoryKiB < highest.memoryKiB || parameters.passes < highest.passes)) {
    return { kind: 'refused', reason: 'below-highest' };
  }
  return { kind: 'accepted', highest: { memoryKiB: parameters.memoryKiB, passes: parameters.passes } };
}

/** A record for a new account (decision 25): version 1, a salt from `random` (`crypto.getRandomValues`), `parameters`. */
export function newKdfRecord(random: (bytes: Uint8Array<ArrayBuffer>) => Uint8Array, parameters: KdfParameters = KDF_DEFAULT): KdfRecord {
  return { version: KDF_V1.version, salt: random(new Uint8Array(KDF_V1.saltBytes)).slice(), memoryKiB: parameters.memoryKiB, passes: parameters.passes };
}

/** The record's fields, exactly these. */
const FIELDS = ['memoryKiB', 'passes', 'salt', 'version'];

/** A whole number of at least one. */
function isCount(value: unknown): value is number {
  return Number.isSafeInteger(value) && Number(value) >= 1;
}

/**
 * Table R: reads a KDF record from a message (structured data from a worker or the relay's decoded answer), field by
 * field: exactly the four fields, a known version, a salt of that version's length, and whole positive parameters.
 * Bounds are {@link checkParameters}' work. The salt is copied, so a buffer shared with the sender can't change it later
 * (R8). Returns null for anything else.
 */
export function readKdfRecord(value: unknown): KdfRecord | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const keys = Object.keys(value).sort();
  if (keys.length !== FIELDS.length || keys.some((key, i) => key !== FIELDS[i])) return null;
  const version: unknown = Reflect.get(value, 'version');
  const salt: unknown = Reflect.get(value, 'salt');
  const memoryKiB: unknown = Reflect.get(value, 'memoryKiB');
  const passes: unknown = Reflect.get(value, 'passes');
  if (version !== KDF_V1.version) return null;
  if (!(salt instanceof Uint8Array) || salt.length !== KDF_V1.saltBytes) return null;
  if (!isCount(memoryKiB) || !isCount(passes)) return null;
  return { version, salt: salt.slice(), memoryKiB, passes };
}
